import { isLaunched } from '@/lib/campaignStatus';
import { channelOfCampaign } from '@/lib/campaignChannel';

// The campaign setup wizard's steps, in order.
//
// The step lives in the URL (`?step=`), not in component state, for the same reason the Campaigns
// page puts its tab in the path: a reload, a shared link and the Back button all land where the
// operator actually was.
//
// The order follows what each step depends on. The template comes before the media because a
// template's media header decides which of the media's assets can be sent and what an upload is
// validated against; the contacts come after both, and the final step only reviews.

export type StepId = 'details' | 'channel' | 'media' | 'audience' | 'launch' | 'results';

export interface StepDef {
  id: StepId;
  labelKey: string;
  label: string;
  hintKey: string;
  hint: string;
}

export const STEPS: readonly StepDef[] = [
  {
    id: 'details',
    labelKey: 'campaigns.steps.details',
    label: 'Basic details',
    hintKey: 'campaigns.steps.details_hint',
    hint: "Give the campaign a name your team will recognise, and a note on what it's for.",
  },
  // Still `channel` in the URL and the code, so existing `?step=channel` links keep working. The
  // channel itself is no longer chosen here — the Campaigns tab decides it — so the step is named for
  // what it actually asks.
  {
    id: 'channel',
    labelKey: 'campaigns.steps.channel',
    label: 'WhatsApp template',
    hintKey: 'campaigns.steps.channel_hint',
    hint: 'Choose the approved WhatsApp template to send.',
  },
  {
    id: 'media',
    labelKey: 'campaigns.steps.media',
    label: 'Choose media',
    hintKey: 'campaigns.steps.media_hint',
    hint: 'Pick the creative to send. The same media can be used by any number of campaigns.',
  },
  {
    id: 'audience',
    labelKey: 'campaigns.steps.audience',
    label: 'Select contacts',
    hintKey: 'campaigns.steps.audience_hint',
    hint: 'Choose who receives this campaign.',
  },
  {
    id: 'launch',
    labelKey: 'campaigns.steps.launch',
    label: 'Final preview & launch',
    hintKey: 'campaigns.steps.launch_hint',
    hint: 'Check everything once more, then launch.',
  },
  {
    id: 'results',
    labelKey: 'campaigns.steps.results',
    label: 'Results',
    hintKey: 'campaigns.steps.results_hint',
    hint: 'What was sent, and how it landed.',
  },
];

export const isStepId = (value: string | null | undefined): value is StepId =>
  STEPS.some((s) => s.id === value);

export const stepIndex = (id: StepId) => STEPS.findIndex((s) => s.id === id);

export const stepDef = (id: StepId) => STEPS[stepIndex(id)];

/**
 * What has to be true before a step can be entered. Each gate guards the step AFTER it: `channel`
 * being satisfied is what opens `media`.
 */
export interface StepGates {
  /** The campaign row exists. False only on /campaigns/new. */
  created: boolean;
  /** An SMS campaign, or a WhatsApp one with a template that can actually be sent. */
  channelValid: boolean;
  /** Media chosen, and for a WhatsApp media header, something to put in it. */
  mediaValid: boolean;
  /** At least one contact would receive it. */
  audienceValid: boolean;
  launched: boolean;
}

export function isStepSatisfied(id: StepId, gates: StepGates): boolean {
  switch (id) {
    case 'details': return gates.created;
    case 'channel': return gates.created && gates.channelValid;
    case 'media': return gates.mediaValid;
    case 'audience': return gates.audienceValid;
    case 'launch': return gates.launched;
    default: return false;
  }
}

/** A step can be entered when every step before it is satisfied. Results only exist after launch. */
export function isStepReachable(id: StepId, gates: StepGates): boolean {
  if (gates.launched) return id === 'results';
  if (id === 'results') return false;
  return STEPS.slice(0, stepIndex(id)).every((s) => isStepSatisfied(s.id, gates));
}

interface ResumableCampaign {
  type?: string;
  status?: string;
  campaignMediaId?: string | null;
  launchConfig?: { channel?: string; whatsappTemplateId?: string | null } | null;
}

/**
 * Where to open a campaign when the URL does not say. Used on load, with what the server saved.
 *
 * The audience is never used here: its recipient count is fetched after mount, and the editor
 * autosaves a default audience the moment it opens, so a saved audience says nothing about whether
 * anybody chose one. A campaign far enough along opens on the final preview instead, whose Edit
 * links reach every step.
 */
export function resumeStep(campaign: ResumableCampaign): StepId {
  if (isLaunched(campaign.status)) return 'results';

  const config = campaign.launchConfig;
  const channel = channelOfCampaign(campaign);
  const hasTemplate = Boolean(config?.whatsappTemplateId);

  if (channel === 'whatsapp' && !hasTemplate) return 'channel';
  if (!campaign.campaignMediaId) return hasTemplate || channel === 'sms' ? 'media' : 'channel';
  return 'launch';
}

/**
 * Whether a step named in the URL can be shown from what the page knows for certain.
 *
 * Reads `campaignMediaId` only, never `launchConfig`: the page fetches the campaign once, while the
 * editor autosaves the config continuously, so the page's copy of it goes stale the moment anything
 * is chosen - gating on it would bounce the operator back a step they have just completed. The
 * template's own requirements are enforced by the live gates instead (Next and the stepper).
 */
export function canOpenFromUrl(id: StepId, campaign: ResumableCampaign): boolean {
  if (isLaunched(campaign.status)) return id === 'results';
  if (id === 'results') return false;
  if (id === 'details' || id === 'channel' || id === 'media') return true;
  return Boolean(campaign.campaignMediaId);
}
